import type {
  GeneratedStudyCard,
  StudyDeckItem,
  StudyImportPreviewRow,
  StudyImportRow,
  StudyNoteInput,
  StudyNoteType,
  StudyTemplateKey
} from '@/types/study'

const TEMPLATE_LABELS: Record<StudyTemplateKey, string> = {
  recognition: 'Recognition',
  production: 'Production',
  cloze: 'Cloze',
  basic: 'Basic'
}

export const NOTE_TYPE_LABELS: Record<StudyNoteType, string> = {
  word: 'Word',
  sentence: 'Sentence',
  cloze: 'Cloze',
  custom: 'Custom'
}

export function getDefaultTemplateKeys(
  noteType: StudyNoteType
): StudyTemplateKey[] {
  if (noteType === 'custom') return ['basic']
  if (noteType === 'cloze') return ['cloze']
  return ['recognition']
}

export function getAvailableTemplateKeys(
  noteType: StudyNoteType
): StudyTemplateKey[] {
  if (noteType === 'word' || noteType === 'sentence') {
    return ['recognition', 'production']
  }
  if (noteType === 'cloze') return ['cloze']
  return ['basic']
}

export function getTemplateLabel(templateKey: StudyTemplateKey) {
  return TEMPLATE_LABELS[templateKey]
}

export function normalizeTemplateKeys(
  noteType: StudyNoteType,
  templateKeys?: StudyTemplateKey[]
): StudyTemplateKey[] {
  const available = getAvailableTemplateKeys(noteType)
  const selected = (
    templateKeys?.length ? templateKeys : getDefaultTemplateKeys(noteType)
  ).filter((key) => available.includes(key))
  return selected.length ? Array.from(new Set(selected)) : [available[0]]
}

export function getStringField(
  fields: Record<string, unknown> | null | undefined,
  key: string
) {
  const value = fields?.[key]
  return typeof value === 'string' ? value.trim() : ''
}

function getDefinitions(fields: Record<string, unknown>) {
  const raw = fields.definitions
  if (Array.isArray(raw)) {
    return raw.filter((item): item is string => typeof item === 'string')
  }
  if (typeof raw === 'string') {
    return raw
      .split(/[;\n]/)
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return []
}

function joinBack(parts: Array<string | null | undefined>) {
  return parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join('\n')
}

function wordFront(fields: Record<string, unknown>) {
  const simplified = getStringField(fields, 'simplified')
  const traditional = getStringField(fields, 'traditional')
  if (traditional && traditional !== simplified)
    return `${simplified} (${traditional})`
  return simplified
}

function sourceTitle(source?: Record<string, unknown>) {
  const value = source?.title ?? source?.source_title
  return typeof value === 'string' ? value.trim() : ''
}

export function generateStudyCards(
  input: StudyNoteInput
): GeneratedStudyCard[] {
  const fields = input.fields || {}
  const source = input.source || {}
  const templateKeys = normalizeTemplateKeys(input.noteType, input.templateKeys)
  const cards: GeneratedStudyCard[] = []

  for (const templateKey of templateKeys) {
    if (input.noteType === 'word') {
      const front = wordFront(fields)
      const definitions = getDefinitions(fields)
      const pinyin = getStringField(fields, 'pinyin')
      const zhuyin = getStringField(fields, 'zhuyin')
      if (templateKey === 'production') {
        cards.push({
          template_key: 'production',
          front: joinBack([definitions[0] || 'Meaning', pinyin]),
          back: front,
          card_data: { note_type: 'word', prompt: 'production' }
        })
      } else {
        cards.push({
          template_key: 'recognition',
          front,
          back: joinBack([pinyin, zhuyin, definitions.join('; ')]),
          card_data: { note_type: 'word', prompt: 'recognition' }
        })
      }
      continue
    }

    if (input.noteType === 'sentence') {
      const text = getStringField(fields, 'text')
      const translation = getStringField(fields, 'translation')
      const sourceName = sourceTitle(source)
      if (templateKey === 'production') {
        cards.push({
          template_key: 'production',
          front: joinBack([
            translation || sourceName || 'Translate this sentence'
          ]),
          back: joinBack([text, sourceName]),
          card_data: { note_type: 'sentence', prompt: 'production' }
        })
      } else {
        cards.push({
          template_key: 'recognition',
          front: text,
          back: joinBack([translation, sourceName]),
          card_data: { note_type: 'sentence', prompt: 'recognition' }
        })
      }
      continue
    }

    if (input.noteType === 'cloze') {
      const text = getStringField(fields, 'text')
      const answer = getStringField(fields, 'answer')
      const translation = getStringField(fields, 'translation')
      const prompt = answer ? text.replace(answer, '____') : text
      cards.push({
        template_key: 'cloze',
        front: prompt || '____',
        back: joinBack([text, translation]),
        card_data: { note_type: 'cloze', answer }
      })
      continue
    }

    const front = getStringField(fields, 'front')
    const back = getStringField(fields, 'back')
    cards.push({
      template_key: 'basic',
      front,
      back,
      card_data: { note_type: 'custom', prompt: 'basic' }
    })
  }

  return cards.filter((card) => card.front.trim())
}

export function buildStudyDedupeKey(input: StudyNoteInput) {
  if (input.noteType === 'word' && input.dictEntryId) {
    return `word:${input.dictEntryId}`
  }
  if (input.noteType === 'sentence') {
    return `sentence:${getStringField(input.fields, 'text').toLowerCase()}`
  }
  if (input.noteType === 'cloze') {
    return `cloze:${getStringField(input.fields, 'text').toLowerCase()}:${getStringField(input.fields, 'answer').toLowerCase()}`
  }
  return `custom:${getStringField(input.fields, 'front').toLowerCase()}:${getStringField(input.fields, 'back').toLowerCase()}`
}

export function validateStudyNoteInput(input: StudyNoteInput): string[] {
  const errors: string[] = []
  if (input.noteType === 'word') {
    if (!input.dictEntryId && !getStringField(input.fields, 'simplified')) {
      errors.push('Word notes need a dictionary entry or Chinese word.')
    }
  } else if (input.noteType === 'sentence') {
    if (!getStringField(input.fields, 'text')) {
      errors.push('Sentence notes need sentence text.')
    }
  } else if (input.noteType === 'cloze') {
    const text = getStringField(input.fields, 'text')
    const answer = getStringField(input.fields, 'answer')
    if (!text) errors.push('Cloze notes need sentence text.')
    if (!answer) errors.push('Cloze notes need a cloze blank answer.')
    if (text && answer && !text.includes(answer)) {
      errors.push('The cloze answer must appear in the sentence.')
    }
  } else {
    if (!getStringField(input.fields, 'front')) {
      errors.push('Custom notes need front text.')
    }
    if (!getStringField(input.fields, 'back')) {
      errors.push('Custom notes need back text.')
    }
  }

  if (!generateStudyCards(input).length) {
    errors.push('At least one generated card needs a front side.')
  }
  return errors
}

function parseCsvLine(line: string) {
  const cells: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i]
    const next = line[i + 1]
    if (char === '"' && quoted && next === '"') {
      cell += '"'
      i += 1
    } else if (char === '"') {
      quoted = !quoted
    } else if (char === ',' && !quoted) {
      cells.push(cell)
      cell = ''
    } else {
      cell += char
    }
  }
  cells.push(cell)
  return cells.map((value) => value.trim())
}

function parseTags(value?: string) {
  return (value || '')
    .split(/[;,]/)
    .map((tag) => tag.trim())
    .filter(Boolean)
}

function parseTemplateKeys(
  noteType: StudyNoteType,
  value?: string
): StudyTemplateKey[] {
  const requested = (value || '')
    .split(/[;,]/)
    .map((key) => key.trim().toLowerCase())
    .filter(Boolean) as StudyTemplateKey[]
  return normalizeTemplateKeys(noteType, requested)
}

export function csvRowToStudyNote(row: StudyImportRow): StudyNoteInput {
  const noteType = row.noteType || 'custom'
  if (noteType === 'word') {
    return {
      noteType,
      fields: {
        simplified: row.simplified || row.front || '',
        traditional: row.traditional || row.simplified || row.front || '',
        pinyin: row.pinyin || '',
        zhuyin: row.zhuyin || '',
        definitions: row.definitions || row.back || ''
      },
      tags: parseTags(row.tags),
      templateKeys: parseTemplateKeys(noteType, row.cardTypes)
    }
  }
  if (noteType === 'sentence') {
    return {
      noteType,
      fields: {
        text: row.text || row.front || '',
        translation: row.translation || row.back || ''
      },
      source: { title: row.sourceTitle || '' },
      tags: parseTags(row.tags),
      templateKeys: parseTemplateKeys(noteType, row.cardTypes)
    }
  }
  if (noteType === 'cloze') {
    return {
      noteType,
      fields: {
        text: row.clozeText || row.text || row.front || '',
        answer: row.clozeAnswer || '',
        translation: row.translation || row.back || ''
      },
      source: { title: row.sourceTitle || '' },
      tags: parseTags(row.tags),
      templateKeys: parseTemplateKeys(noteType, row.cardTypes)
    }
  }
  return {
    noteType: 'custom',
    fields: {
      front: row.front || row.text || '',
      back: row.back || row.translation || ''
    },
    source: { title: row.sourceTitle || '' },
    tags: parseTags(row.tags),
    templateKeys: ['basic']
  }
}

export function parseStudyCsv(csv: string): StudyImportPreviewRow[] {
  const lines = csv
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((line) => line.trim())
  if (!lines.length) return []
  const headers = parseCsvLine(lines[0]).map((header) =>
    header.trim().toLowerCase()
  )

  return lines.slice(1).map((line, index) => {
    const values = parseCsvLine(line)
    const raw: Record<string, string> = {}
    headers.forEach((header, headerIndex) => {
      raw[header] = values[headerIndex] || ''
    })
    const noteType = (raw.note_type || raw.notetype || raw.type || 'custom')
      .trim()
      .toLowerCase() as StudyNoteType
    const input = csvRowToStudyNote({
      noteType,
      front: raw.front,
      back: raw.back,
      text: raw.text,
      translation: raw.translation,
      simplified: raw.simplified,
      traditional: raw.traditional,
      pinyin: raw.pinyin,
      zhuyin: raw.zhuyin,
      definitions: raw.definitions,
      clozeText: raw.cloze_text,
      clozeAnswer: raw.cloze_answer,
      sourceTitle: raw.source_title,
      tags: raw.tags,
      cardTypes: raw.card_types
    })
    const typeOk = ['word', 'sentence', 'cloze', 'custom'].includes(noteType)
    const errors = typeOk
      ? validateStudyNoteInput(input)
      : [`Unsupported note type: ${noteType}`]
    return {
      rowNumber: index + 2,
      input: typeOk ? input : null,
      errors
    }
  })
}

function csvEscape(value: unknown) {
  const text = String(value ?? '')
  if (/[",\n\r\t]/.test(text)) return `"${text.replace(/"/g, '""')}"`
  return text
}

function tsvEscape(value: unknown) {
  return String(value ?? '')
    .replace(/\t/g, ' ')
    .replace(/\r?\n/g, '<br>')
}

export function studyItemsToCsv(items: StudyDeckItem[]) {
  const headers = [
    'note_type',
    'card_type',
    'front',
    'back',
    'tags',
    'source_title'
  ]
  const rows = items.map((item) => [
    item.note_type,
    item.template_key,
    item.front,
    item.back || '',
    Array.isArray(item.fields.tags) ? item.fields.tags.join(';') : '',
    sourceTitle(item.source)
  ])
  return [headers, ...rows]
    .map((row) => row.map(csvEscape).join(','))
    .join('\n')
}

export function studyItemsToAnkiTsv(items: StudyDeckItem[]) {
  const headers = ['Front', 'Back', 'Tags', 'Source']
  const rows = items.map((item) => [
    item.front,
    item.back || '',
    Array.isArray(item.fields.tags) ? item.fields.tags.join(' ') : '',
    sourceTitle(item.source)
  ])
  return [headers, ...rows]
    .map((row) => row.map(tsvEscape).join('\t'))
    .join('\n')
}

export const STUDY_CSV_TEMPLATE = [
  'note_type,front,back,text,translation,simplified,traditional,pinyin,zhuyin,definitions,cloze_text,cloze_answer,source_title,tags,card_types',
  'sentence,你好！,Hello!,你好！,Hello!,,,,,,,,First lesson,greeting,recognition;production',
  'cloze,,,,I like drinking tea.,,,,,,我喜欢喝茶。,喝茶,Daily sentence,food,cloze',
  'custom,What does 明天 mean?,tomorrow,,,,,,,,,,,time,basic'
].join('\n')
